import * as React from 'react';
import { useState } from 'react';
import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import { cn, Icon } from '@lqa-frontend/ui-system';

const inputVariants = cva(
	'flex w-full items-center gap-8 bg-transparent text-text-primary',
	{
		variants: {
			view: {
				default:
					'border-1 border-outline-neutral transition-[background-color,border-color] hover:not-focus-within:bg-base-2 focus-within:border-interactive focus-within:inset-ring-1 focus-within:inset-ring-interactive has-[:disabled]:bg-base-2 has-[:disabled]:cursor-not-allowed',
				plain: 'rounded-none border-0 has-[:disabled]:cursor-not-allowed',
			},
			size: {
				md: 'text-14 rounded-md',
				sm: 'text-12 rounded-sm',
			},
		},
		compoundVariants: [
			{ view: 'default', size: 'md', class: 'h-36 px-12 py-8' },
			{ view: 'default', size: 'sm', class: 'h-32 px-10 py-6' },
			{ view: 'plain', class: 'h-36 my-0 py-0' },
		],
		defaultVariants: { view: 'default', size: 'md' },
	}
);

type InputVariant = 'default' | 'search' | 'password';
type InputSize = NonNullable<VariantProps<typeof inputVariants>['size']>;

interface Props
	extends
		Omit<React.ComponentProps<'input'>, 'size'>,
		VariantProps<typeof inputVariants> {
	variant?: InputVariant;
	/** Classes applied to the outer wrapper (border, ring, sizing). */
	wrapperClassName?: string;
}

const InputBox = React.forwardRef<HTMLInputElement, Props>(
	(
		{
			className,
			wrapperClassName,
			variant = 'default',
			view,
			size,
			type,
			placeholder,
			...props
		},
		ref
	) => {
		const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);
		const isSearch = variant === 'search';
		const isPassword = variant === 'password';

		const resolvedType = isPassword
			? isPasswordVisible
				? 'text'
				: 'password'
			: (type ?? 'text');

		return (
			<div className={cn(inputVariants({ view, size }), wrapperClassName)}>
				{isSearch && (
					<Icon
						name="search"
						size="s"
						className={cn(
							'  shrink-0 text-text-secondary',
							view === 'plain' ? 'ml-12' : 'ml-0'
						)}
					/>
				)}
				<input
					ref={ref}
					type={resolvedType}
					data-slot="input"
					placeholder={placeholder ?? (isSearch ? 'Search' : undefined)}
					className={cn(
						'min-w-0 flex-1 bg-transparent p-0 outline-none ring-0 placeholder:text-text-secondary',
						className
					)}
					{...props}
				/>
				{isPassword && (
					<button
						type="button"
						onClick={() => setIsPasswordVisible((shown) => !shown)}
						aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
						className="shrink-0 cursor-pointer text-neutral"
					>
						{isPasswordVisible ? (
							<Icon name="eye" size="s" />
						) : (
							<Icon name="eyeSlash" size="s" />
						)}
					</button>
				)}
			</div>
		);
	}
);

InputBox.displayName = 'InputBox';

export { InputBox };
export type { InputVariant, InputSize };
